import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, AlertCircle, ArrowRight } from "lucide-react";
import axios from "axios";
import { API, BACKEND_URL } from "../config/api";


export default function ResumeBookingPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [booking, setBooking] = useState(null);
  const [agent, setAgent] = useState(null);

  useEffect(() => {
    loadBookingData();
  }, [bookingId]);

  const loadBookingData = async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}/resume`);
      setBooking(response.data.booking);
      setAgent(response.data.agent);
      
      // Store booking data in session for the services page
      sessionStorage.setItem("bookingData", JSON.stringify({
        ...response.data.booking,
        agent_id: response.data.agent?.id,
        agent_slug: response.data.agent?.agent_slug,
        agent_name: response.data.agent?.agent_name,
        show_yachts: response.data.agent?.show_yachts,
        show_yachts_by_destination: response.data.agent?.show_yachts_by_destination || {},
        show_water_experiences: response.data.agent?.show_water_experiences,
        button_color: response.data.agent?.button_color
      }));
      
      sessionStorage.setItem("agentContext", JSON.stringify({
        id: response.data.agent?.id,
        agent_slug: response.data.agent?.agent_slug,
        agent_name: response.data.agent?.agent_name,
        agent_type: response.data.agent?.agent_type,
        logo_url: response.data.agent?.logo_url,
        brand_styles: response.data.agent?.brand_styles,
        enabled_destinations: response.data.agent?.enabled_destinations || ["mallorca", "ibiza"],
        show_yachts: response.data.agent?.show_yachts,
        show_yachts_by_destination: response.data.agent?.show_yachts_by_destination || {},
        show_water_experiences: response.data.agent?.show_water_experiences,
        button_color: response.data.agent?.button_color
      }));
      
      setLoading(false);
    } catch (err) {
      console.error("Failed to load booking:", err);
      setError(err.response?.data?.detail || "Unable to load your booking. It may have already been submitted or the link has expired.");
      setLoading(false);
    }
  };

  const handleContinue = () => {
    // Navigate to services page with the booking data already loaded in sessionStorage
    navigate("/services");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your booking...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-card rounded-2xl p-8 text-center border border-border shadow-lg"
        >
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-semibold text-foreground mb-2">Unable to Resume</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:opacity-90 transition-opacity"
          >
            Return Home
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-lg w-full bg-card rounded-2xl p-8 border border-border shadow-lg"
      >
        {/* Agent Logo */}
        {agent?.logo_url && (
          <div className="flex justify-center mb-6">
            <img 
              src={agent.logo_url.startsWith('/') ? `${BACKEND_URL}${agent.logo_url}` : agent.logo_url}
              alt={agent.agent_name}
              className="h-12 object-contain"
            />
          </div>
        )}

        <h1 className="text-2xl font-serif text-foreground text-center mb-2">
          Welcome Back, {booking?.first_name}!
        </h1>
        <p className="text-muted-foreground text-center mb-8">
          {booking?.plan_sent
            ? `Add more to your ${(booking?.destination || "").charAt(0).toUpperCase() + (booking?.destination || "").slice(1)} concierge plan`
            : `Continue planning your ${(booking?.destination || "").charAt(0).toUpperCase() + (booking?.destination || "").slice(1)} experience`
          }
        </p>

        {/* Booking Summary */}
        <div className="bg-secondary/30 rounded-xl p-5 mb-6 space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Reference</span>
            <span className="font-medium text-foreground">{booking?.reference_number}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Destination</span>
            <span className="font-medium text-foreground capitalize">{booking?.destination}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Dates</span>
            <span className="font-medium text-foreground">
              {booking?.arrival_date && new Date(booking.arrival_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
              {' – '}
              {booking?.departure_date && new Date(booking.departure_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          </div>
          {booking?.villa_name && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Accommodation</span>
              <span className="font-medium text-foreground">{booking.villa_name}</span>
            </div>
          )}
        </div>

        {/* Continue Button */}
        <button
          onClick={handleContinue}
          style={{ backgroundColor: agent?.button_color || '#8B7355' }}
          className="w-full py-4 rounded-xl text-white font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
          data-testid="resume-continue-btn"
        >
          {booking?.plan_sent ? "Add More to My Plan" : "Continue Planning"}
          <ArrowRight className="w-5 h-5" />
        </button>

        <p className="text-xs text-muted-foreground text-center mt-4">
          {booking?.plan_sent
            ? "Your submitted selections are saved — browse and add more services"
            : "Your previous selections have been saved"
          }
        </p>
      </motion.div>
    </div>
  );
}
